/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { observer } from 'mobx-react';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { RichText } from '@msdyn365-commerce/core';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { IHerbalOrderInfographicData } from './herbal-order-infographic.data';
import { IHerbalOrderInfographicProps } from './herbal-order-infographic.props.autogenerated';

export interface IHerbalOrderInfographicViewProps extends IHerbalOrderInfographicProps<IHerbalOrderInfographicData> {
    moduleProps: IModuleProps;
    containerProps: INodeProps;
    contentContainerProps: INodeProps;
    rowProps: INodeProps;
    steps: {
        step1: React.ReactNode;
        step2: React.ReactNode;
        step3: React.ReactNode;
    };
}

/**
 * HerbalOrderInfographic component
 * @extends {React.PureComponent<IHerbalOrderInfographicProps<IHerbalOrderInfographicData>>}
 */
@observer
class HerbalOrderInfographic extends React.PureComponent<IHerbalOrderInfographicProps<IHerbalOrderInfographicData>> {
    private readonly moduleName: string = 'herbal-order-infographic';

    public render(): JSX.Element {
        const { config } = this.props;
        const viewProps: IHerbalOrderInfographicViewProps = {
            ...this.props,
            moduleProps: {
                moduleProps: this.props,
                className: classname(this.moduleName, config.className)
            },
            containerProps: {
                className: `${this.moduleName}__container`
            },
            contentContainerProps: {
                className: `${this.moduleName}__content-container`
            },
            rowProps: {
                className: `${this.moduleName}__row`
            },
            steps: {
                step1: this._renderStep(
                    config.step1Title || 'TAKE OUR CONSULTATION',
                    config.step1Icon || 'phone-check',
                    config.step1Number || '01',
                    config.step1Description,
                    false
                ),
                step2: this._renderStep(
                    config.step2Title || 'YOUR FORMULAS ARE MADE TO ORDER',
                    config.step2Icon || 'dropper',
                    config.step2Number || '02',
                    config.step2Description,
                    true,
                    config.step2CtaText || 'GET YOUR FORMULA',
                    config.step2CtaLink || '#'
                ),
                step3: this._renderStep(
                    config.step3Title || 'AS YOU CHANGE, SO DOES YOUR PROSE',
                    config.step3Icon || 'star-refresh',
                    config.step3Number || '03',
                    config.step3Description,
                    false
                )
            }
        };

        return this.props.renderView(viewProps) as JSX.Element;
    }

    private _renderStep(
        title: string,
        icon: string,
        stepNumber: string,
        description: RichText | undefined,
        hasCta: boolean = false,
        ctaText?: string,
        ctaLink?: string
    ): React.ReactElement {
        return (
            <div className={`${this.moduleName}__step`}>
                <div className={`${this.moduleName}__step-icon-container`}>
                    <i className={`msc-icon msc-icon-${icon}`} aria-hidden='true'></i>
                </div>
                <div className={`${this.moduleName}__step-number-container`}>
                    <h3 className={`${this.moduleName}__step-title`}>{title}</h3>
                    {/* <div className={`${this.moduleName}__step-number`}>{stepNumber}</div> */}
                </div>
                <div className={`${this.moduleName}__step-description`} dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(description)} />
                {/* {hasCta && ctaText && (
                    <a href={ctaLink} className={`${this.moduleName}__cta-button`}>
                        {ctaText}
                    </a>
                )} */}
            </div>
        );
    }
}

export default HerbalOrderInfographic;
